import axios from "axios";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "aamg.access_token";

export const api = axios.create({
  baseURL: API_BASE,
  withCredentials: true,
});

// Bearer-token fallback for iOS Safari (ITP blocks cross-site cookies). We
// store the JWT returned by /auth/login + /auth/register + /auth/google/session
// in localStorage and attach it as Authorization on every request alongside
// the cookies. The backend honors either credential.
export function setAccessToken(token) {
  try {
    if (token) {
      window.localStorage.setItem(TOKEN_KEY, token);
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      window.localStorage.removeItem(TOKEN_KEY);
      delete api.defaults.headers.common.Authorization;
    }
  } catch {
    // localStorage may be unavailable in private mode — silently ignore.
    if (token) {
      api.defaults.headers.common.Authorization = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common.Authorization;
    }
  }
}

export function getAccessToken() {
  try { return window.localStorage.getItem(TOKEN_KEY); } catch { return null; }
}

// Restore the token on module load so the first /auth/me probe carries it.
const _bootToken = getAccessToken();
if (_bootToken) {
  api.defaults.headers.common.Authorization = `Bearer ${_bootToken}`;
}

// Module-scoped callback so AuthContext can install its own handler when the user state needs clearing.
let onUnauthorized = null;
export const setUnauthorizedHandler = (cb) => { onUnauthorized = cb; };

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (error?.response?.status === 401) {
      // Ignore 401s from /auth/me probe — that's expected when the user isn't logged in yet
      const url = error.config?.url || "";
      const isAuthProbe = url.endsWith("/auth/me") || url.endsWith("/auth/login");
      if (!isAuthProbe && onUnauthorized) {
        try { onUnauthorized(error); } catch {}
      }
    }
    return Promise.reject(error);
  }
);

export function formatApiError(detail, fallback = "Something went wrong") {
  if (!detail) return fallback;
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail) && detail.length) {
    const first = detail[0];
    if (typeof first === "string") return first;
    if (first?.msg) return `${(first.loc || []).slice(-1)[0] || ""}: ${first.msg}`.trim();
  }
  if (typeof detail === "object" && typeof detail.msg === "string") return detail.msg;
  return fallback;
}
